import React, { useMemo, useRef } from 'react';
import { useEditor, Node } from '@splashthat/splash-cms-engine';
import PropTypes from 'prop-types';

import ConditionalWrap from 'common/hoc/ConditionalWrap';
import TouchpointContainer from 'common/components/TouchpointEditor/components/TouchpointContainer';
import { transformStructureDataForEditor } from 'common/compounds/VirtualEventRenderer/states/lib/transformStructureDataForEditor';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import BreakpointPreviewer from 'common/components/TouchpointEditor/components/BreakpointPreviewer';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import useMediaQuery from 'utils/hooks/useMediaquery';
import { createMaxWidthQuery } from 'common/styles/breakpoints';

import {
    NODE_ID_PAGE_CONTAINER,
    NODE_ID_HEADER,
    NODE_ID_HEADLINE,
    NODE_ID_LOGO,
    NODE_ID_VIRTUAL_CONTENT,
    NODE_ID_FOOTER_CONTAINER,
    NODE_ID_CONTACT_ORGANIZER_BUTTON,
    NODE_ID_VISIT_EVENT_PAGE_BUTTON,
    NODE_ID_CUSTOM_BUTTON,
    NODE_ID_CUSTOM_BUTTON_TWO,
    NODE_ID_EMBED,
    NODE_ID_ALT_EMBED,
    NODE_ID_LINK_OUT,
    NODE_ID_ZOOM_LINK_OUT,
    NODE_DISPLAY_NAME_PAGE_CONTAINER,
    NODE_DISPLAY_NAME_HEADER,
    NODE_DISPLAY_NAME_HEADLINE,
    NODE_DISPLAY_NAME_VIRTUAL_CONTENT,
    NODE_DISPLAY_NAME_LOGO,
    NODE_DISPLAY_NAME_FOOTER_CONTAINER,
    NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON,
    NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON,
    NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO,
    NODE_DISPLAY_NAME_EMBED,
    NODE_DISPLAY_NAME_ALT_EMBED,
    NODE_DISPLAY_NAME_LINK_OUT,
    NODE_DISPLAY_NAME_ZOOM_LINK_OUT,
    SIDEBAR_LAYOUT,
    VIRTUAL_CONTENT_TYPE_LINK_OUT,
} from '../constants';
import EditorTools from '../components/sidebar';
import {
    VirtualGuestExperienceContainer,
    FooterBlockContainer,
    LeftLinkContainer,
    RightLinkContainer,
} from '../../components/Common.styles';
import PageContainer from '../../components/nodes/Block/SubTypes/PageContainer';
import FooterContainer from '../../components/nodes/Block/SubTypes/FooterContainer';
import Logo from '../../components/nodes/Logo/SubTypes/ContentLogo';
import ViewEventPageLink from '../../components/nodes/Link/SubTypes/ViewEventPageLink';
import ContactTheOrganizer from '../../components/nodes/Link/SubTypes/ContactTheOrganizer';
import CustomButtonLight from '../../components/nodes/Button/SubTypes/CustomButtonLight';
import Embed from './nodes/Embed';
import ZoomLinkOut from './nodes/ZoomLinkOut';
import LinkOut from './nodes/LinkOut';

import Header from './nodes/Header';
import VirtualContent from './nodes/VirtualContent';
import SidebarEmbedPortal, { Portal } from './nodes/VirtualContent/SidebarEmbedPortal';
import EventTitleHeadline from './nodes/Headline/SubTypes/EventTitleHeadline';

import { PrimaryEmbedContainer } from './DuringEvent.styles';

export default function DuringEvent({ structure, currentPageState, guest }) {
    const isMedium = useMediaQuery(createMaxWidthQuery('medium'));
    const editor = useEditor((state) => ({
        editing: state.editing,
        virtualContentLayout: state?.nodes?.[NODE_ID_VIRTUAL_CONTENT]?.meta?.layout,
        virtualContentType: state?.nodes?.[NODE_ID_VIRTUAL_CONTENT]?.meta?.virtualContentType,
    }));

    const duringEventPageStructure = useMemo(
        () => transformStructureDataForEditor(structure, currentPageState),
        [structure, currentPageState],
    );

    const { embeds, event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });

    const isLinkOut = editor.virtualContentType === VIRTUAL_CONTENT_TYPE_LINK_OUT;
    const shouldRenderAltEmbed = embeds.data.shouldRenderAltEmbed && !isLinkOut && embeds.data.hasAltEmbed;
    const isVirtualContentSidebarLayout =
        (!isMedium || (isMedium && editor.editing)) &&
        !isLinkOut &&
        editor.virtualContentLayout === SIDEBAR_LAYOUT &&
        embeds.data.hasAltEmbed;

    const shouldOpenPortal = shouldRenderAltEmbed && isVirtualContentSidebarLayout;

    const sidebarPortalRef = useRef(null);

    return (
        <TouchpointContainer editing={editor.editing}>
            <BreakpointPreviewer enabled={shouldPreview}>
                <VirtualGuestExperienceContainer
                    $editing={editor.editing}
                    $isSidebarOpen={shouldOpenPortal}
                    $previewing={shouldPreview}
                >
                    <Node
                        is={PageContainer}
                        id={NODE_ID_PAGE_CONTAINER}
                        displayName={NODE_DISPLAY_NAME_PAGE_CONTAINER}
                        {...duringEventPageStructure[NODE_ID_PAGE_CONTAINER]}
                    >
                        <Node
                            is={Header}
                            id={NODE_ID_HEADER}
                            displayName={NODE_DISPLAY_NAME_HEADER}
                            {...duringEventPageStructure[NODE_ID_HEADER]}
                        >
                            <Node
                                is={EventTitleHeadline}
                                id={NODE_ID_HEADLINE}
                                displayName={NODE_DISPLAY_NAME_HEADLINE}
                                {...duringEventPageStructure[NODE_ID_HEADLINE]}
                            />
                            <Node
                                is={Logo}
                                id={NODE_ID_LOGO}
                                displayName={NODE_DISPLAY_NAME_LOGO}
                                {...duringEventPageStructure[NODE_ID_LOGO]}
                            />
                        </Node>
                        <Node
                            is={VirtualContent}
                            id={NODE_ID_VIRTUAL_CONTENT}
                            displayName={NODE_DISPLAY_NAME_VIRTUAL_CONTENT}
                            {...duringEventPageStructure[NODE_ID_VIRTUAL_CONTENT]}
                        >
                            <ConditionalWrap
                                condition={!isLinkOut}
                                wrap={(children) => <PrimaryEmbedContainer>{children}</PrimaryEmbedContainer>}
                            >
                                <Node
                                    is={Embed}
                                    id={NODE_ID_EMBED}
                                    displayName={NODE_DISPLAY_NAME_EMBED}
                                    guest={guest}
                                    {...duringEventPageStructure[NODE_ID_EMBED]}
                                />
                                <Node
                                    is={ZoomLinkOut}
                                    id={NODE_ID_ZOOM_LINK_OUT}
                                    displayName={NODE_DISPLAY_NAME_ZOOM_LINK_OUT}
                                    {...duringEventPageStructure[NODE_ID_ZOOM_LINK_OUT]}
                                />
                            </ConditionalWrap>
                            <Portal portalComponentRef={sidebarPortalRef} isPortalOpened={shouldOpenPortal}>
                                <Node
                                    is={Embed}
                                    id={NODE_ID_ALT_EMBED}
                                    displayName={NODE_DISPLAY_NAME_ALT_EMBED}
                                    {...duringEventPageStructure[NODE_ID_ALT_EMBED]}
                                    isSecondary
                                />
                            </Portal>
                            <Node
                                is={LinkOut}
                                id={NODE_ID_LINK_OUT}
                                displayName={NODE_DISPLAY_NAME_LINK_OUT}
                                {...duringEventPageStructure[NODE_ID_LINK_OUT]}
                            />
                        </Node>
                        <Node
                            is={FooterContainer}
                            id={NODE_ID_FOOTER_CONTAINER}
                            displayName={NODE_DISPLAY_NAME_FOOTER_CONTAINER}
                            {...duringEventPageStructure[NODE_ID_FOOTER_CONTAINER]}
                        >
                            <FooterBlockContainer>
                                <LeftLinkContainer>
                                    <li>
                                        <Node
                                            is={ContactTheOrganizer}
                                            id={NODE_ID_CONTACT_ORGANIZER_BUTTON}
                                            displayName={NODE_DISPLAY_NAME_CONTACT_ORGANIZER_BUTTON}
                                            {...duringEventPageStructure[NODE_ID_CONTACT_ORGANIZER_BUTTON]}
                                        />
                                    </li>
                                    <li>
                                        <Node
                                            is={ViewEventPageLink}
                                            id={NODE_ID_VISIT_EVENT_PAGE_BUTTON}
                                            displayName={NODE_DISPLAY_NAME_VISIT_EVENT_PAGE_BUTTON}
                                            {...duringEventPageStructure[NODE_ID_VISIT_EVENT_PAGE_BUTTON]}
                                        />
                                    </li>
                                </LeftLinkContainer>
                                <RightLinkContainer>
                                    <Node
                                        is={CustomButtonLight}
                                        id={NODE_ID_CUSTOM_BUTTON}
                                        displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON}
                                        {...duringEventPageStructure[NODE_ID_CUSTOM_BUTTON]}
                                    />
                                    <Node
                                        is={CustomButtonLight}
                                        id={NODE_ID_CUSTOM_BUTTON_TWO}
                                        displayName={NODE_DISPLAY_NAME_CUSTOM_BUTTON_TWO}
                                        {...duringEventPageStructure[NODE_ID_CUSTOM_BUTTON_TWO]}
                                    />
                                </RightLinkContainer>
                            </FooterBlockContainer>
                        </Node>
                    </Node>
                    <SidebarEmbedPortal ref={sidebarPortalRef} isOpen={shouldOpenPortal} />
                </VirtualGuestExperienceContainer>
            </BreakpointPreviewer>
            {editor.editing && <EditorTools />}
        </TouchpointContainer>
    );
}

DuringEvent.propTypes = {
    structure: PropTypes.object,
    guest: PropTypes.object,
    currentPageState: PropTypes.string.isRequired,
};

DuringEvent.defaultProps = {
    structure: null,
    guest: {},
};
